import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChefHat, 
  Shield, 
  Lock, 
  Eye, 
  Database, 
  Users, 
  Mail, 
  Phone,
  CreditCard,
  FileText,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const PrivacyPolicy = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const lastUpdated = "December 15, 2024";

  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: Database,
      color: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      content: [
        {
          subtitle: 'Personal Information',
          text: 'We collect personal information that you provide directly to us, including:',
          items: [
            'Name and contact information (email address, phone number)',
            'Delivery address and location data',
            'Payment information (processed securely through third-party providers)',
            'Order history and preferences',
            'Communication preferences and feedback'
          ]
        },
        {
          subtitle: 'Automatically Collected Information',
          text: 'We automatically collect certain information when you use our services:',
          items: [
            'Device information (IP address, browser type, operating system)',
            'Usage data (pages visited, time spent, features used)',
            'Location data (with your permission)',
            'Cookies and similar tracking technologies'
          ]
        }
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: Users,
      color: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      content: [
        {
          subtitle: 'Service Provision',
          text: 'We use your information to:',
          items: [
            'Process and fulfill your food orders',
            'Provide customer support and respond to inquiries',
            'Send order confirmations and delivery updates',
            'Process payments and prevent fraud',
            'Improve our services and user experience'
          ]
        },
        {
          subtitle: 'Communication',
          text: 'We may use your contact information to:',
          items: [
            'Send promotional offers and marketing communications (with your consent)',
            'Notify you about new menu items and special offers',
            'Send important service updates and policy changes',
            'Respond to your feedback and complaints'
          ]
        }
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: Shield,
      color: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
      content: [
        {
          subtitle: 'Third-Party Service Providers',
          text: 'We may share your information with trusted third parties who assist us in:',
          items: [
            'Payment processing (Razorpay, Stripe)',
            'Delivery services and logistics partners',
            'Customer support and communication platforms',
            'Analytics and performance monitoring',
            'Marketing and advertising services'
          ]
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information when required by law or to:',
          items: [
            'Comply with legal obligations and court orders',
            'Protect our rights, property, and safety',
            'Prevent fraud and ensure security',
            'Investigate potential violations of our terms'
          ]
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      color: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement industry-standard security measures to protect your information:',
          items: [
            'SSL encryption for all data transmission',
            'Secure payment processing with PCI DSS compliance',
            'Regular security audits and vulnerability assessments',
            'Access controls and employee training',
            'Secure data storage and backup systems'
          ]
        },
        {
          subtitle: 'Data Retention',
          text: 'We retain your information for as long as necessary to:',
          items: [
            'Provide our services and fulfill orders',
            'Comply with legal and regulatory requirements',
            'Resolve disputes and enforce agreements',
            'Improve our services and user experience'
          ]
        }
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights and Choices',
      icon: Eye,
      color: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      content: [
        {
          subtitle: 'Access and Control',
          text: 'You have the right to:',
          items: [
            'Access and review your personal information',
            'Correct inaccurate or incomplete data',
            'Request deletion of your personal information',
            'Opt-out of marketing communications',
            'Withdraw consent for data processing'
          ]
        },
        {
          subtitle: 'Data Portability',
          text: 'You can request:',
          items: [
            'A copy of your personal data in a structured format',
            'Transfer of your data to another service provider',
            'Information about how your data is processed',
            'Details about third parties with whom we share data'
          ]
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      icon: FileText,
      color: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      content: [
        {
          subtitle: 'Types of Cookies',
          text: 'We use different types of cookies to enhance your experience:',
          items: [
            'Essential cookies (required for basic functionality)',
            'Performance cookies (analytics and optimization)',
            'Functional cookies (personalization and preferences)',
            'Marketing cookies (targeted advertising and promotions)'
          ]
        },
        {
          subtitle: 'Cookie Management',
          text: 'You can control cookies through:',
          items: [
            'Your browser settings and privacy controls',
            'Our cookie consent banner and preferences',
            'Third-party opt-out tools and services',
            'Mobile device privacy settings'
          ]
        }
      ]
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      details: ['privacy@btechbawarchi.com', 'support@btechbawarchi.com']
    },
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 8107436861', 'Mon-Fri: 9:00 AM - 6:00 PM']
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
      color: '#333',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>B.Tech Bawarchi</h1>
          </div>
          <nav style={{ display: 'flex', gap: '24px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Home</Link>
            <Link to="/about" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>About</Link>
            <Link to="/contact" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Contact</Link>
            <Link to="/menu" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Menu</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        padding: '60px 16px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          transition: 'all 1s ease',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)'
            }}>
              <Shield size={40} color="white" />
            </div>
          </div>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1f2937',
            margin: '0 0 16px 0',
            letterSpacing: '-0.025em',
            lineHeight: '1.1'
          }}>
            Privacy{' '}
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Policy
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 24px 0'
          }}>
            Your privacy is important to us. This policy explains how we collect, 
            use, and protect your personal information.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <FileText size={16} color="#6b7280" />
            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
              Last updated: {lastUpdated}
            </span>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={{
        padding: '60px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          transition: 'all 1s ease 0.2s',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid #bae6fd',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <CheckCircle size={24} color="#0ea5e9" />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#0c4a6e',
                margin: 0
              }}>
                Our Commitment to Privacy
              </h3>
            </div>
            <p style={{
              fontSize: '16px',
              color: '#0c4a6e',
              lineHeight: '1.6',
              margin: 0
            }}>
              At B.Tech Bawarchi, we are committed to protecting your privacy and ensuring the security 
              of your personal information. This Privacy Policy explains our practices regarding the 
              collection, use, and disclosure of information when you use our food delivery services.
            </p>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid #f59e0b',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <AlertTriangle size={24} color="#d97706" />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#92400e',
                margin: 0
              }}>
                Important Notice
              </h3>
            </div>
            <p style={{
              fontSize: '16px',
              color: '#92400e',
              lineHeight: '1.6',
              margin: 0
            }}>
              By using our services, you agree to the collection and use of information in accordance 
              with this Privacy Policy. If you do not agree with our practices, please do not use our services.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{
        padding: '60px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {sections.map((section, index) => (
            <div
              key={section.id}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '40px',
                marginBottom: '40px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                transition: `all 1s ease ${0.4 + index * 0.1}s`,
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: section.color,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                }}>
                  <section.icon size={28} color="white" />
                </div>
                <h3 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#1f2937',
                  margin: 0
                }}>
                  {section.title}
                </h3>
              </div>
              
              {section.content.map((content, contentIndex) => (
                <div key={contentIndex} style={{ marginBottom: '32px' }}>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#374151',
                    margin: '0 0 16px 0'
                  }}>
                    {content.subtitle}
                  </h4>
                  <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    margin: '0 0 16px 0'
                  }}>
                    {content.text}
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {content.items.map((item, itemIndex) => (
                      <li key={itemIndex} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '12px',
                        fontSize: '16px',
                        color: '#6b7280',
                        lineHeight: '1.6'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#f97316',
                          marginTop: '8px',
                          flexShrink: 0
                        }}></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section style={{
        padding: '60px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          transition: 'all 1s ease 1.2s',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <h3 style={{
            fontSize: '32px',
            fontWeight: '800',
            color: '#1f2937',
            margin: '0 0 16px 0'
          }}>
            Questions About Privacy?
          </h3>
          <p style={{
            fontSize: '18px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 40px 0'
          }}>
            If you have any questions about this Privacy Policy or our data practices, 
            please don't hesitate to contact us.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '32px',
            marginBottom: '40px'
          }}>
            {contactInfo.map((info, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <info.icon size={24} color="#f97316" />
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: 0
                  }}>
                    {info.title}
                  </h4>
                </div>
                {info.details.map((detail, idx) => (
                  <p key={idx} style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    margin: '0 0 8px 0',
                    lineHeight: '1.5'
                  }}>
                    {detail}
                  </p>
                ))}
              </div>
            ))}
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
            borderRadius: '16px',
            padding: '32px',
            color: 'white'
          }}>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: '0 0 16px 0'
            }}>
              Data Protection Officer
            </h4>
            <p style={{
              fontSize: '16px',
              opacity: 0.9,
              lineHeight: '1.6',
              margin: 0
            }}>
              For privacy-related concerns and data protection inquiries, you can reach our 
              Data Protection Officer at privacy@btechbawarchi.com
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#1f2937',
        color: 'white',
        padding: '48px 16px 24px 16px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '8px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0
            }}>B.Tech Bawarchi</h4>
          </div>
          <p style={{
            color: '#9ca3af',
            marginBottom: '24px',
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            Serving authentic Indian cuisine with love and tradition since 2019.
          </p>
          <div style={{
            borderTop: '1px solid #374151',
            paddingTop: '16px',
            color: '#9ca3af',
            fontSize: '12px'
          }}>
            <p style={{ margin: 0 }}>&copy; 2024 B.Tech Bawarchi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
